import type {
  StorefrontTagMoveRequestType,
  StorefrontTagMoveResponseType,
} from "@sellar-io/api-v2/types";
import { Button, InputField, InputText } from "@sellar-io/components";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useId, useState } from "react";
import { useNavigate } from "react-router";
import { Alert } from "../../../../../components/alert/Alert";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../../../../components/modal/Dialog";
import { LAYOUT_GROUPING } from "../../../../../constants/routes";
import {
  apiV2AssertError,
  apiV2AssertResponse,
  apiV2Request,
} from "../../../../../hooks/api/v2/apiV2Request";
import { ServerStateKeys } from "../../../../../hooks/api/v2/ServerStateKeys";
import { useStorefrontTagList } from "../../../../../hooks/api/v2/storefrontTags/useStorefrontTagList";
import { useFirebaseUser } from "../../../../../hooks/useFirebaseUser.shared";
import { useSellarCurrentBusiness } from "../../../../../hooks/useSellarCurrentBusiness.shared";
import { useSellarSupplier } from "../../../../../hooks/useSellarSupplier";

export const StorefrontUpdateProductTagModal = ({
  onDismiss,
  productId,
  productName,
  storefrontTagName,
  currentGroupId,
  isLastProduct,
}: {
  onDismiss: () => void;
  productId: number;
  productName: string;
  storefrontTagName: string;
  currentGroupId: number;
  isLastProduct: boolean;
}) => {
  const { business } = useSellarCurrentBusiness();
  const { user } = useFirebaseUser();
  const supplier = useSellarSupplier();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const id = useId();
  const [destinationId, setDestinationId] = useState<number | "new" | "">("");
  const [newGroupName, setNewGroupName] = useState("");
  const tagsQuery = useStorefrontTagList({
    query: [{ type: "SupplierId", value: supplier.id, modifier: "EQUALS" }],
  });
  const groups = (tagsQuery.data?.pages.flat() ?? []).filter(
    (tag) => tag.id !== currentGroupId,
  );
  const moveMutation = useMutation({
    mutationFn: (destination: StorefrontTagMoveRequestType["destination"]) =>
      apiV2Request({
        user,
        business,
        method: "post",
        path: `storefront-tags/${currentGroupId}/move-products`,
        data: { ProductIds: [productId], destination, deleteSource: false },
      }).then(
        (response) =>
          apiV2AssertResponse<StorefrontTagMoveResponseType>(response).data,
      ),
    onSuccess: async (result) => {
      if (result.sourceDeleted) navigate(LAYOUT_GROUPING, { replace: true });
      await Promise.all([
        ...[
          ServerStateKeys.StorefrontTags,
          ServerStateKeys.Products,
          ServerStateKeys.UngroupedProducts,
        ].map((key) =>
          queryClient.invalidateQueries({
            queryKey: [user?.uid, business?.id, key],
          }),
        ),
        queryClient.invalidateQueries({
          queryKey: [business?.id, ServerStateKeys.Products],
        }),
      ]);
      onDismiss();
    },
  });
  const canSubmit =
    !moveMutation.isPending &&
    (destinationId === "new"
      ? newGroupName.trim().length > 0
      : !tagsQuery.isLoading &&
        !tagsQuery.isError &&
        groups.some((group) => group.id === destinationId));
  const dismiss = () => {
    if (!moveMutation.isPending) onDismiss();
  };
  return (
    <Dialog active onDismiss={dismiss}>
      <DialogNavigation
        leftIcon="CHEVRON_DOWN"
        onClickLeftIcon={dismiss}
        title={`Move ${productName} to another group`}
        description={`This product will be removed from ${storefrontTagName}`}
      />
      <DialogContent className="flex flex-col gap-4 p-4">
        {moveMutation.isError && (
          <Alert
            type="error"
            message={
              apiV2AssertError(moveMutation.error)?.response?.data.message ??
              "The product could not be moved. Try again."
            }
          />
        )}
        {tagsQuery.isError && (
          <Alert type="error" message="Groups could not be loaded.">
            <Button
              disabled={moveMutation.isPending}
              onClick={() => void tagsQuery.refetch()}
            >
              Try again
            </Button>
          </Alert>
        )}
        {!tagsQuery.isLoading &&
          !tagsQuery.isError &&
          groups.length === 0 &&
          !tagsQuery.hasNextPage && (
            <Alert
              type="information"
              message="There are no other groups yet. Choose ‘Create a new group’ below to move this product."
            />
          )}
        <div className="flex flex-col gap-2">
          <label className="font-medium" htmlFor={`${id}-destination`}>
            Choose a group
          </label>
          <select
            id={`${id}-destination`}
            className="h-10 rounded border border-zinc-200 bg-white px-3"
            value={destinationId}
            disabled={moveMutation.isPending}
            onChange={(event) => {
              const value = event.target.value;
              setDestinationId(
                value === "new" || value === "" ? value : Number(value),
              );
            }}
          >
            <option value="">
              {tagsQuery.isLoading ? "Loading groups…" : "Select..."}
            </option>
            {groups.map((group) => (
              <option key={group.id} value={group.id}>
                {group.value}
              </option>
            ))}
            <option value="new">Create a new group</option>
          </select>
        </div>
        {tagsQuery.hasNextPage && (
          <Button
            color="transparent"
            disabled={moveMutation.isPending}
            loading={tagsQuery.isFetchingNextPage}
            onClick={() => void tagsQuery.fetchNextPage()}
          >
            Load more groups
          </Button>
        )}
        {destinationId === "new" && (
          <InputField label="New group name">
            <InputText
              autoFocus
              value={newGroupName}
              disabled={moveMutation.isPending}
              onChange={(event) => setNewGroupName(event.target.value)}
              placeholder="Type a name..."
            />
          </InputField>
        )}
        {isLastProduct && (
          <Alert
            type="warning"
            message="Moving this last product will delete the empty group and return you to Layout & Grouping."
          />
        )}
      </DialogContent>
      <DialogCTA>
        <Button
          loading={moveMutation.isPending}
          disabled={!canSubmit}
          onClick={() => {
            if (!canSubmit) return;
            if (destinationId === "new")
              moveMutation.mutate({ type: "new", name: newGroupName.trim() });
            else if (typeof destinationId === "number")
              moveMutation.mutate({ type: "group", id: destinationId });
          }}
        >
          Move product
        </Button>
      </DialogCTA>
    </Dialog>
  );
};
